import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import useAxiosInstance from "../../hooks/useAxios";

const MessageBubble = ({ msg, onSave }) => {
  const isUser = msg.role === "user";
  const isMarkdown = msg.content?.type === "markdown";
  const originalMarkdown = msg.content?.data?.markdown ?? "";

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(originalMarkdown);
  const [saving, setSaving] = useState(false);
  const axiosInstance = useAxiosInstance();

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.post(`/chats/messages/sandbox/edit/${msg.id}/`, {
        updated_content: {
          data: { markdown: draft },
          type: "markdown",
        },
      });
      onSave(msg.id, draft);
      setEditing(false);
      toast.success("Message saved");
    } catch {
      toast.error("Failed to save message");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(originalMarkdown);
    setEditing(false);
  };

  const bubbleContent = () => {
    if (isUser) {
      const text =
        typeof msg.content === "string"
          ? msg.content
          : msg.content?.data?.text ?? msg.content?.data?.markdown ?? "";
      return <p className="text-sm whitespace-pre-wrap break-words">{text}</p>;
    }

    if (!isMarkdown) {
      return (
        <span className="text-xs text-gray-500 italic">
          [type: {msg.content?.type ?? "unknown"}]
        </span>
      );
    }

    if (editing) {
      return (
        <textarea
          className="w-full text-sm border rounded p-2 focus:outline-none focus:ring-1 focus:ring-gray-400 resize-y min-h-[80px]"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
        />
      );
    }

    return (
      <p className="text-sm whitespace-pre-wrap break-words">
        {originalMarkdown}
      </p>
    );
  };

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      <div className={`max-w-[70%] ${isUser ? "items-end" : "items-start"} flex flex-col`}>
        <div
          className={`px-4 py-2 rounded-2xl ${
            isUser
              ? "bg-gray-200 rounded-br-sm"
              : "bg-white border rounded-bl-sm"
          }`}
        >
          {bubbleContent()}
        </div>

        {!isUser && isMarkdown && (
          <div className="mt-1 flex gap-2">
            {editing ? (
              <>
                <button
                  disabled={saving}
                  onClick={handleSave}
                  className="text-xs px-2 py-0.5 bg-black text-white rounded disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  disabled={saving}
                  onClick={handleCancel}
                  className="text-xs px-2 py-0.5 bg-gray-200 rounded"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => setEditing(true)}
                className="text-xs px-2 py-0.5 bg-gray-100 border rounded hover:bg-gray-200"
              >
                Edit
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ChatEditor = () => {
  const axiosInstance = useAxiosInstance();
  const [chatId, setChatId] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleLoad = async () => {
    if (!chatId.trim()) {
      toast.error("Please enter a Chat ID");
      return;
    }
    setLoading(true);
    setMessages([]);
    try {
      const res = await axiosInstance.get(`/chats/${chatId}/`);
      let msgs = res.data?.messages ?? [];
      msgs = msgs.map((msg) => {
        if (
          msg.role === "user" &&
          Array.isArray(msg.content) &&
          msg.content.length === 1
        ) {
          return { ...msg, content: msg.content[0] };
        }
        return msg;
      });
      setMessages(msgs);
      if (msgs.length === 0) toast.info("No messages found");
    } catch {
      toast.error("Failed to load chat");
    } finally {
      setLoading(false);
    }
  };

  const handleMessageSave = (messageId, newMarkdown) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId
          ? {
              ...msg,
              content: {
                ...msg.content,
                data: { ...msg.content.data, markdown: newMarkdown },
              },
            }
          : msg
      )
    );
  };

  return (
    <div className="w-full h-full flex flex-col p-5">
      <p className="text-2xl font-semibold mb-4">Chat Editor</p>

      <div className="flex gap-2 mb-4 max-w-xl">
        <input
          className="border rounded-lg px-3 py-2 flex-1 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
          placeholder="Enter Chat ID"
          value={chatId}
          onChange={(e) => setChatId(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleLoad()}
        />
        <button
          disabled={loading}
          onClick={handleLoad}
          className="px-3 py-2 text-sm font-medium text-white bg-black rounded-lg disabled:opacity-50"
        >
          {loading ? "Loading..." : "Load"}
        </button>
      </div>

      {messages.length > 0 && (
        <div className="flex-1 overflow-y-auto border rounded-xl bg-gray-50 p-4">
          {messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              msg={msg}
              onSave={handleMessageSave}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatEditor;
